import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EiPiGrade — Phase 0 Workbench Shell (2026-05-29 · Rohit).
 *
 * The look-and-feel here mirrors `/eipigrade-spike.html` 1:1 — same IBM Plex
 * Mono/Sans, teal #5fd0c5 accent on near-black #0a0b0d, dark right-side
 * control panel. The two surfaces share a visual language so the operator
 * never feels like they've switched apps when launching the WebGPU spike.
 *
 * Responsibilities:
 *   • Manage GradeRecipe documents (save, list, brand-tag)
 *   • Upload a clip → S3 + first-frame extraction
 *   • SAM2 point-prompt segmentation on the first frame
 *   • Queue server-side full-res renders (stub backend Phase 0)
 *   • Launch the WebGPU spike (`/eipigrade-spike.html`) for live grading
 *
 * Out of scope (lives in the spike HTML / OpenReel fork):
 *   • WebGPU canvas viewport, WGSL shader chain, live scopes, curve widget
 */
export default function EiPiGradeWorkbench() {
  const navigate = useNavigate();
  const [clip, setClip] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [recipeName, setRecipeName] = useState('Untitled Grade');
  const [brandTag, setBrandTag] = useState('');
  const [layer, setLayer] = useState({
    id: 'layer-1', name: 'Primary', enabled: true,
    white_balance: { temperature: 0, tint: 0 },
    lift_gamma_gain: { lift: [0, 0, 0], gamma: [0, 0, 0], gain: [0, 0, 0], offset: [0, 0, 0] },
    opacity: 1.0,
  });

  const [samPoints, setSamPoints] = useState([]);
  const [samMaskB64, setSamMaskB64] = useState('');
  const [samMode, setSamMode] = useState(false);
  const [samRunning, setSamRunning] = useState(false);
  const [showMask, setShowMask] = useState(true);

  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [savedRecipes, setSavedRecipes] = useState([]);

  const fileInputRef = useRef(null);
  const frameImgRef = useRef(null);

  useEffect(() => { loadSavedRecipes(); }, []);

  const loadSavedRecipes = async () => {
    try {
      const r = await axios.get(`${API}/recipes/eipigrade/recipes`);
      setSavedRecipes(r.data || []);
    } catch (e) { /* first-time users have nothing yet */ }
  };

  const onPickClip = () => fileInputRef.current?.click();

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', f);
    try {
      const r = await axios.post(`${API}/recipes/eipigrade/upload-clip`, form);
      setClip(r.data);
      setSamPoints([]); setSamMaskB64('');
      toast.success(`Uploaded ${r.data.filename} · ${r.data.width}×${r.data.height} · ${r.data.duration_seconds.toFixed(1)}s`);
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onFrameClick = (e) => {
    if (!samMode || !clip || !frameImgRef.current) return;
    const rect = frameImgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const label = e.shiftKey ? 0 : 1;
    setSamPoints(p => [...p, { x, y, label }]);
  };

  const runSAM = async () => {
    if (!clip?.first_frame_url || samPoints.length === 0) return;
    if (!samPoints.some(p => p.label === 1)) {
      toast.error('Add at least one POSITIVE point (left-click on the subject)');
      return;
    }
    setSamRunning(true);
    try {
      const r = await axios.post(`${API}/recipes/eipigrade/sam/segment-frame`, {
        frame_url: clip.first_frame_url,
        points: samPoints,
      });
      setSamMaskB64(r.data.mask_png_b64);
      toast.success(`SAM2 mask returned · ${r.data.points_used} point(s) used`);
    } catch (err) {
      toast.error('SAM failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSamRunning(false);
    }
  };

  const buildRecipe = () => ({
    recipe_version: 1,
    name: recipeName,
    brand_tag: brandTag || null,
    input_transform: 'sRGB - linear',
    output_transform: 'Output - sRGB',
    layers: [layer],
    source_clip_url: clip?.clip_url,
    source_clip_filename: clip?.filename,
    is_look_bible_entry: false,
  });

  const onSaveRecipe = async () => {
    if (!clip) { toast.error('Upload a clip first'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/recipes/eipigrade/recipes`, buildRecipe());
      toast.success('Grade recipe saved');
      loadSavedRecipes();
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const onQueueRender = async () => {
    if (!clip) { toast.error('Upload a clip first'); return; }
    setRendering(true);
    try {
      const r = await axios.post(`${API}/recipes/eipigrade/render`, {
        clip_url: clip.clip_url,
        recipe: buildRecipe(),
        output_format: 'prores_422',
      });
      toast.info(`Render queued · job ${r.data.id.slice(0, 8)} · ${r.data.status} (worker pending Phase 1)`);
    } catch (err) {
      toast.error('Render queue failed: ' + (err.response?.data?.detail || err.message));
    } finally { setRendering(false); }
  };

  const onExportJSON = () => {
    const r = buildRecipe();
    const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${recipeName.replace(/\s+/g, '_')}.recipe.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const onLaunchSpike = () => {
    // Spike is a same-origin static page → it reads the JWT from
    // localStorage by itself. No token-passing needed.
    window.open('/eipigrade-spike.html', '_blank', 'noopener,noreferrer');
  };

  // ── Shared inline styles to match the spike HTML's IBM Plex aesthetic ──
  const C = {
    bg: '#0a0b0d', panel: '#131519', panel2: '#0f1114',
    line: '#23262d', ink: '#e7e9ec', inkDim: '#8b9099', inkFaint: '#565b63',
    accent: '#5fd0c5', accentDim: '#2f6b66', warn: '#e0a458', neutral: '#1a1c20',
    mono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
    sans: "'IBM Plex Sans', system-ui, sans-serif",
  };

  return (
    <div
      data-testid="eipigrade-workbench"
      style={{
        minHeight: '100vh', background: C.bg, color: C.ink,
        fontFamily: C.sans, fontSize: 13,
      }}
    >
      {/* IBM Plex font load — same as the spike */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600&display=swap"
        rel="stylesheet"
      />
      {/* Slider thumb styling — inline styles can't target ::-webkit pseudo. */}
      <style>{`
        .eg-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%; height: 3px; border-radius: 3px;
          background: ${C.line}; outline: none; cursor: pointer;
        }
        .eg-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 13px; height: 13px; border-radius: 50%;
          background: ${C.accent};
          border: 2px solid ${C.bg};
          box-shadow: 0 0 0 1px ${C.accentDim};
        }
        .eg-slider::-moz-range-thumb {
          width: 13px; height: 13px; border-radius: 50%;
          background: ${C.accent};
          border: 2px solid ${C.bg};
          box-shadow: 0 0 0 1px ${C.accentDim};
        }
        .eg-input::placeholder { color: ${C.inkFaint}; }
        .eg-input:focus { border-color: ${C.accentDim} !important; }
        .eg-btn:hover:not(:disabled) { border-color: ${C.accentDim}; color: ${C.accent}; }
        .eg-btn-primary:hover:not(:disabled) { filter: brightness(1.15); }
        .eg-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>

      {/* ─── Header ────────────────────────────────────────────────── */}
      <header
        style={{
          display: 'flex', alignItems: 'center', gap: 18,
          padding: '10px 16px', borderBottom: `1px solid ${C.line}`,
          background: C.panel2,
        }}
      >
        <button
          onClick={() => navigate('/recipes')}
          data-testid="eipigrade-back"
          style={btnStyle(C, { fontSize: 11 })}
          onMouseEnter={(e) => { e.currentTarget.style.color = C.accent; e.currentTarget.style.borderColor = C.accentDim; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = C.ink; e.currentTarget.style.borderColor = C.line; }}
        >
          ← Recipes
        </button>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <b style={{ fontFamily: C.mono, fontWeight: 600, letterSpacing: '.5px', fontSize: 14 }}>
            EiPiGrade
          </b>
          <span style={{
            fontFamily: C.mono, fontSize: 10, color: C.accent,
            border: `1px solid ${C.accentDim}`, padding: '2px 6px',
            borderRadius: 3, letterSpacing: 1,
          }}>
            PHASE 0 SPIKE
          </span>
        </div>

        <div style={{ flex: 1 }} />

        {/* Stats — match the spike's header */}
        <Stat C={C} label="CLIP" value={clip ? `${clip.width}×${clip.height}` : '—'} />
        <Stat C={C} label="DUR" value={clip ? `${clip.duration_seconds?.toFixed(1)}s` : '—'} />
        <Stat C={C} label="SAVED" value={String(savedRecipes.length)} />

        <button
          onClick={onLaunchSpike}
          data-testid="eipigrade-launch-spike"
          style={btnStyle(C, { primary: true, marginLeft: 8 })}
          title="Open the WebGPU live-preview spike in a new tab"
        >
          ▶ Launch WebGPU Spike
        </button>
      </header>

      {/* ─── Phase 0 banner ─────────────────────────────────────────── */}
      <div
        data-testid="eipigrade-phase0-banner"
        style={{
          padding: '8px 16px', background: 'rgba(95,208,197,.05)',
          borderBottom: `1px solid ${C.accentDim}40`,
          fontFamily: C.mono, fontSize: 11, color: C.inkDim,
        }}
      >
        <b style={{ color: C.accent }}>Phase 0 spike</b> — Recipe CRUD, clip upload and SAM masking
        are live. Live WebGPU grading lives in the spike at{' '}
        <code style={{ fontFamily: C.mono, color: C.ink }}>/eipigrade-spike.html</code> · render
        worker (ffmpeg + OCIO 2.5) pending Phase 1.
      </div>

      {/* ─── Main layout: viewport (left) + control panel (right) ───── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 348px',
          height: 'calc(100vh - 96px)',
          minHeight: 0,
        }}
      >
        {/* ── Viewport ───────────────────────────────────────────── */}
        <div
          style={{
            display: 'grid', gridTemplateRows: '1fr auto',
            minHeight: 0, background: '#000',
          }}
        >
          <div
            style={{
              position: 'relative', minHeight: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'repeating-conic-gradient(#0d0d0d 0% 25%, #111 0% 50%) 50%/22px 22px',
              cursor: samMode ? 'crosshair' : 'default',
            }}
          >
            {!clip ? (
              <div
                style={{
                  display: 'flex', flexDirection: 'column', gap: 14,
                  alignItems: 'center', justifyContent: 'center',
                  textAlign: 'center', color: C.inkDim, padding: 24,
                }}
              >
                <h2 style={{ fontFamily: C.mono, fontWeight: 500, color: C.ink, margin: 0, fontSize: 15 }}>
                  Drop a clip to grade
                </h2>
                <p style={{ margin: 0, maxWidth: 430, lineHeight: 1.5 }}>
                  Upload a 1080p proxy (H.264 / ProRes, ≤200MB). First frame is extracted
                  for SAM segmentation; the recipe applies to the full-res render server-side.
                </p>
                <button
                  onClick={onPickClip}
                  disabled={uploading}
                  data-testid="eipigrade-upload-cta"
                  style={btnStyle(C, { primary: true })}
                >
                  {uploading ? 'Uploading…' : 'Choose file…'}
                </button>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 6 }}>
                  {['SAM2 mask', 'Look Bible', 'recipe.json', 'server render', 'WebGPU spike'].map(b => (
                    <span key={b} style={{
                      fontFamily: C.mono, fontSize: 10, color: C.inkFaint,
                      border: `1px solid ${C.line}`, padding: '3px 7px', borderRadius: 3,
                    }}>{b}</span>
                  ))}
                </div>
              </div>
            ) : (
              <div
                onClick={onFrameClick}
                style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%' }}
              >
                <img
                  ref={frameImgRef}
                  src={clip.first_frame_url}
                  alt="frame"
                  data-testid="eipigrade-frame"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                />
                {showMask && samMaskB64 && (
                  <img
                    src={`data:image/png;base64,${samMaskB64}`}
                    alt="mask"
                    style={{
                      position: 'absolute', inset: 0, width: '100%', height: '100%',
                      objectFit: 'contain', pointerEvents: 'none',
                      mixBlendMode: 'screen', opacity: 0.5,
                    }}
                  />
                )}
                {samPoints.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      position: 'absolute', width: 12, height: 12, borderRadius: '50%',
                      border: '2px solid #fff', pointerEvents: 'none',
                      transform: 'translate(-50%, -50%)',
                      left: `${p.x * 100}%`, top: `${p.y * 100}%`,
                      background: p.label === 1 ? '#4ade80' : '#f43f5e',
                    }}
                  />
                ))}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              hidden
              onChange={onUpload}
              data-testid="eipigrade-file-input"
            />
          </div>

          {/* SAM control strip — bottom of viewport */}
          {clip && (
            <div
              style={{
                borderTop: `1px solid ${C.line}`, background: C.panel2,
                padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 12,
                fontFamily: C.mono, fontSize: 10, color: C.inkDim,
              }}
            >
              <span style={{ color: C.inkFaint, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                SAM · {clip.filename}
              </span>
              <button
                onClick={() => setSamMode(m => !m)}
                data-testid="eipigrade-sam-mode-toggle"
                style={btnStyle(C, { active: samMode, fontSize: 10 })}
              >
                {samMode ? 'SAM on · click subject' : 'Enable SAM'}
              </button>
              {samPoints.length > 0 && (
                <>
                  <span>
                    <b style={{ color: '#4ade80' }}>{samPoints.filter(p => p.label === 1).length}</b> include ·{' '}
                    <b style={{ color: '#f43f5e' }}>{samPoints.filter(p => p.label === 0).length}</b> exclude
                  </span>
                  <span style={{ color: C.inkFaint }}>(Shift-click = exclude)</span>
                  <button
                    onClick={() => { setSamPoints([]); setSamMaskB64(''); }}
                    data-testid="eipigrade-sam-clear"
                    style={btnStyle(C, { fontSize: 10 })}
                  >
                    Clear
                  </button>
                  <button
                    onClick={runSAM}
                    disabled={samRunning}
                    data-testid="eipigrade-sam-run"
                    style={btnStyle(C, { primary: true, fontSize: 10 })}
                  >
                    {samRunning ? 'Segmenting…' : 'Run SAM2'}
                  </button>
                </>
              )}
              {samMaskB64 && (
                <button
                  onClick={() => setShowMask(s => !s)}
                  data-testid="eipigrade-toggle-mask"
                  style={{ ...btnStyle(C, { fontSize: 10 }), marginLeft: 'auto' }}
                >
                  Mask {showMask ? 'on' : 'off'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Right control panel ────────────────────────────────── */}
        <aside
          style={{
            borderLeft: `1px solid ${C.line}`, background: C.panel,
            overflowY: 'auto', padding: '4px 0 60px',
          }}
        >
          {/* Recipe metadata */}
          <Group C={C} title="Recipe">
            <label style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 1, color: C.inkFaint, textTransform: 'uppercase' }}>
              Name
            </label>
            <input
              value={recipeName}
              onChange={e => setRecipeName(e.target.value)}
              placeholder="Untitled Grade"
              data-testid="eipigrade-recipe-name"
              className="eg-input"
              style={inputStyle(C)}
            />
            <label style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 1, color: C.inkFaint, textTransform: 'uppercase', marginTop: 10, display: 'block' }}>
              Brand tag (optional)
            </label>
            <input
              value={brandTag}
              onChange={e => setBrandTag(e.target.value)}
              placeholder="e.g. Tide"
              data-testid="eipigrade-brand-tag"
              className="eg-input"
              style={inputStyle(C)}
            />
          </Group>

          {/* Tone */}
          <Group C={C} title="Tone">
            <Slider
              C={C} label="Temperature"
              value={layer.white_balance.temperature} min={-100} max={100} step={1}
              onChange={v => setLayer(l => ({ ...l, white_balance: { ...l.white_balance, temperature: v } }))}
              testid="eipigrade-temperature"
            />
            <Slider
              C={C} label="Tint"
              value={layer.white_balance.tint} min={-100} max={100} step={1}
              onChange={v => setLayer(l => ({ ...l, white_balance: { ...l.white_balance, tint: v } }))}
              testid="eipigrade-tint"
            />
          </Group>

          {/* Primaries — ASC-CDL Lift / Gamma / Gain */}
          <Group C={C} title="Primaries · ASC-CDL">
            <RGBTriplet
              C={C} label="Lift"
              values={layer.lift_gamma_gain.lift}
              onChange={v => setLayer(l => ({ ...l, lift_gamma_gain: { ...l.lift_gamma_gain, lift: v } }))}
              testid="eipigrade-lift"
            />
            <RGBTriplet
              C={C} label="Gamma"
              values={layer.lift_gamma_gain.gamma}
              onChange={v => setLayer(l => ({ ...l, lift_gamma_gain: { ...l.lift_gamma_gain, gamma: v } }))}
              testid="eipigrade-gamma"
            />
            <RGBTriplet
              C={C} label="Gain"
              values={layer.lift_gamma_gain.gain}
              onChange={v => setLayer(l => ({ ...l, lift_gamma_gain: { ...l.lift_gamma_gain, gain: v } }))}
              testid="eipigrade-gain"
            />
          </Group>

          {/* Actions */}
          <Group C={C} title="Actions">
            <button
              onClick={onSaveRecipe}
              disabled={!clip || saving}
              data-testid="eipigrade-save-recipe"
              style={{ ...btnStyle(C, { primary: true }), width: '100%', marginBottom: 8 }}
            >
              {saving ? 'Saving…' : 'Save Recipe'}
            </button>
            <button
              onClick={onQueueRender}
              disabled={!clip || rendering}
              data-testid="eipigrade-queue-render"
              style={{ ...btnStyle(C), width: '100%', marginBottom: 8 }}
            >
              {rendering ? 'Queueing…' : 'Render Full-Res'}
            </button>
            <button
              onClick={onExportJSON}
              disabled={!clip}
              data-testid="eipigrade-export-json"
              style={{ ...btnStyle(C), width: '100%' }}
            >
              Export recipe.json
            </button>
          </Group>

          {/* Saved recipes */}
          {savedRecipes.length > 0 && (
            <Group C={C} title={`Saved (${savedRecipes.length})`}>
              <ul
                data-testid="eipigrade-saved-list"
                style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}
              >
                {savedRecipes.slice(0, 12).map(r => (
                  <li
                    key={r.id}
                    style={{
                      fontFamily: C.mono, fontSize: 11, color: C.inkDim,
                      padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 8,
                      borderRadius: 3,
                    }}
                  >
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.name}
                    </span>
                    {r.brand_tag && (
                      <span style={{
                        fontFamily: C.mono, fontSize: 9, color: C.accent,
                        border: `1px solid ${C.accentDim}`, padding: '1px 5px', borderRadius: 2,
                      }}>
                        {r.brand_tag}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Group>
          )}

          <div style={{ padding: '14px 16px', color: C.inkFaint, fontSize: 11, lineHeight: 1.55 }}>
            Live WebGPU grading (match-to-reference, secondary qualifier, scopes,{' '}
            <code style={{ fontFamily: C.mono, color: C.inkDim }}>.cube</code> export) lives in
            the <b style={{ color: C.inkDim }}>WebGPU Spike</b> — open it from the header.
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── Reusable styled atoms ──────────────────────────────────────────

const btnStyle = (C, opts = {}) => ({
  fontFamily: C.mono,
  fontSize: opts.fontSize || 11,
  letterSpacing: '.4px',
  cursor: 'pointer',
  background: opts.primary ? C.accentDim : (opts.active ? C.accentDim : C.neutral),
  color: opts.primary || opts.active ? '#eafffb' : C.ink,
  border: `1px solid ${opts.primary || opts.active ? C.accent : C.line}`,
  padding: '7px 12px',
  borderRadius: 5,
  transition: '.12s',
  marginLeft: opts.marginLeft || 0,
  marginBottom: opts.marginBottom || 0,
  opacity: 1,
});

const inputStyle = (C) => ({
  width: '100%', marginTop: 4,
  background: C.neutral, color: C.ink,
  border: `1px solid ${C.line}`,
  borderRadius: 4, padding: '6px 8px',
  fontFamily: C.sans, fontSize: 12,
  outline: 'none',
});
const Stat = ({ C, label, value }) => (
  <div style={{ fontFamily: C.mono, fontSize: 11, color: C.inkDim, display: 'flex', gap: 6, alignItems: 'center' }}>
    <span>{label}</span>
    <b style={{ color: C.ink, fontWeight: 600 }}>{value}</b>
  </div>
);

const Group = ({ C, title, children }) => (
  <div style={{ borderBottom: `1px solid ${C.line}`, padding: '14px 16px' }}>
    <h3 style={{
      fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5,
      color: C.inkFaint, margin: '0 0 12px', textTransform: 'uppercase',
      fontWeight: 500,
    }}>
      {title}
    </h3>
    {children}
  </div>
);

const Slider = ({ C, label, value, min, max, step = 0.01, onChange, testid }) => (
  <div style={{ marginBottom: 13 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
      <span style={{ color: C.inkDim, fontSize: 12 }}>{label}</span>
      <span style={{ fontFamily: C.mono, fontSize: 11, color: C.ink }}>
        {(typeof value === 'number' ? value.toFixed(step >= 1 ? 0 : 2) : value)}
      </span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      data-testid={testid}
      className="eg-slider"
    />
  </div>
);

const RGBTriplet = ({ C, label, values, onChange, testid }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{
      fontFamily: C.mono, fontSize: 9, letterSpacing: 1, color: C.inkFaint,
      margin: '4px 0 8px', textTransform: 'uppercase',
    }}>
      {label}
    </div>
    {['R', 'G', 'B'].map((ch, i) => (
      <Slider
        key={ch} C={C} label={ch}
        value={values[i]} min={-1} max={1} step={0.01}
        onChange={v => onChange(values.map((vv, ii) => ii === i ? v : vv))}
        testid={`${testid}-${ch.toLowerCase()}`}
      />
    ))}
  </div>
);
